import { inject, Injectable } from '@angular/core';
import { EventHttpService } from '@core/http/event-http.service';
import { FormEditorService } from '@core/services/form-editor.service';
import { PopupBlockerDetectionService } from '@core/services/popup-blocker-detection.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DynamicField } from '@shared/components/dynamic-form/models/field';
import { EventQueryFormDialogComponent } from '@shared/components/event-query-form-dialog/event-query-form-dialog.component';
import { UrlParam } from '@shared/enums/url-param.enum';
import { EventInterface } from '@shared/interfaces/event.interface';
import { EditableForm } from '@shared/models/form';
import { FormUtils } from '@shared/utils/form-utils';
import { TimeUtils } from '@shared/utils/time-utils';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EventPaymentService {
  private readonly modalService = inject(NgbModal);
  private readonly formEditorService = inject(FormEditorService);

  constructor(
    private readonly sessionService: SessionService,
    private readonly eventHttpService: EventHttpService,
    private readonly popupBlockerDetectionService: PopupBlockerDetectionService
  ) {}

  handleEventPayment(event: EventInterface, utmSource?: string) {
    this.withQueryForm(event, jsonFormData =>
      this.checkoutEvent(event, utmSource, jsonFormData)
    );
  }

  withQueryForm(
    event: EventInterface,
    onProceed: (jsonFormData?: string) => void
  ): void {
    this.fetchQueryFormFields(event.id, fields => {
      if (!fields.length) {
        onProceed();
        return;
      }

      const modalRef = this.openQueryFormDialog(fields);

      modalRef.result.then(
        (result: { action: 'submitted' | 'skip'; value?: unknown }) => {
          onProceed(
            result.action === 'submitted'
              ? JSON.stringify(result.value)
              : undefined
          );
        },
        () => undefined
      );
    });
  }

  private fetchQueryFormFields(
    eventId: number,
    onFields: (fields: DynamicField[]) => void
  ): void {
    this.eventHttpService
      .getQueryForm(eventId)
      .pipe(catchError(() => of({ jsonForm: '' })))
      .subscribe((res: { jsonForm: string }) => {
        const form = this.formEditorService.setFormValidators(
          JSON.parse(res?.jsonForm || '{}') as EditableForm
        );
        onFields(form?.fields ?? []);
      });
  }

  private openQueryFormDialog(fields: DynamicField[]): NgbModalRef {
    const modalRef = this.modalService.open(EventQueryFormDialogComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
    });
    modalRef.componentInstance.fields = fields;

    return modalRef;
  }

  private checkoutEvent(
    event: EventInterface,
    utmSource?: string,
    jsonFormData?: string
  ): void {
    const targetDateTimeZoneOffset = TimeUtils.getTargetDateTimezone(
      event.eventDateTime
    );

    this.eventHttpService
      .checkout(event.id, {
        targetTimezone: targetDateTimeZoneOffset,
        utmSource,
        ...(jsonFormData && { jsonFormData }),
      })
      .subscribe({
        next: (res: string) => {
          const registerEventUrl = this.sessionService.appendLanguagePath(
            `${environment.jipUrl.event}/${event.id}`
          );

          this.sessionService.setLoginCallBackpage(registerEventUrl);
          const newWin = window.open(res, '_blank');
          const popupBlocked =
            !newWin || newWin.closed || newWin.closed == undefined;
          this.popupBlockerDetectionService.handlePopupBlockerDetection(newWin);

          this.goBack(popupBlocked);
        },
      });
  }

  private goBack(isPopupBlocked = false): void {
    let url = this.sessionService.getLoginCallBackpage() || location.origin;

    if (isPopupBlocked) {
      url = url + `?${UrlParam.PopUpBlocked}=true`;
    }

    FormUtils.navigateTo(url);
  }
}
